import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
//@ts-ignore
import FileIcon from "../../../assets/images/file.png";
import CustomInput from "../../../components/CustomInput/CustomInput";
import { useForm } from "react-hook-form";
import { Search } from "lucide-react";
import { getJobRequirement } from "../../../endpoints/hiringCompany/requirements";
import LoadingAnimation from "../../../assets/svg/LoadingAnimation";
import { colors } from "../../../utils/theme";
import dayjs from "dayjs";
import {
  getJobStatusLabel,
  isNullOrEmpty,
} from "../../../utils/helperFunction";
import { EnumJobStatus, HiringCompanyType, JobStatus } from "@utils/constants";
import { useSelector } from "react-redux";
import { RootState } from "@store/reducers";

const Requirements: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  const {
    control,
    formState: { errors },
  } = useForm();
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const hasData = data?.length > 0;
  const currentRole = Number(user?.hiringCompanyType);
  const isTeamMember = currentRole === HiringCompanyType.TEAM_MEMBER;
  const isAdminOrSuperAdmin =
    currentRole === HiringCompanyType.ADMIN ||
    currentRole === HiringCompanyType.SUPER_ADMIN;

  const isOwner = (job: any) => {
    const createdBy = job?.createdBy?._id || job?.createdBy;
    return String(createdBy || "") === String(user?._id || "");
  };

  const canEditJob = (job: any) => isAdminOrSuperAdmin || isOwner(job);

  useEffect(() => {
    fetchJobRequirements();
  }, []);

  const fetchJobRequirements = useCallback(async () => {
    try {
      const response = await getJobRequirement();
      //@ts-ignore
      setData(
        response?.data?.sort((a, b) => {
          if (!a.publishedDate && !b.publishedDate) return 0; // both missing
          if (!a.publishedDate) return -1; // a goes on top
          if (!b.publishedDate) return 1; // b goes on top
          //@ts-ignore
          return new Date(b.publishedDate) - new Date(a.publishedDate); // newest first
        }),
      );
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  if (isLoading) {
    return (
      <div className="flex flex-col rounded-lg bg-white w-full flex-grow p-6 justify-center items-center">
        <LoadingAnimation color={colors.PrimaryBlack} />
      </div>
    );
  }

  return (
    <div className="flex flex-col rounded-lg bg-white w-full flex-grow p-6">
      {hasData ? (
        <div className="overflow-x-auto no-scrollbar">
          <div className="flex  items-center justify-between w-full gap-8 bg-[#F1F5F9] rounded-lg px-3 py-3 mb-3">
            <div className="relative w-1/3">
              {/* @ts-ignore */}
              <CustomInput
                name="search"
                control={control}
                className="border-none"
                errors={errors}
                placeholder="Search by job title"
                renderRight={<Search color="#6F6F6F" width={16} />}
              />
            </div>
            {!isTeamMember ? (
              <button
                className="bg-[#4D4D4D] text-white px-3 py-[10px] font-semibold rounded-md text-[10px]"
                onClick={() => navigate("/requirements/job/create")}
              >
                Submit job requirement
              </button>
            ) : null}
          </div>
          <table className="min-w-full text-xs text-left">
            <thead className="border-b border-[#E0E0E0]">
              <tr className="font-semibold">
                <th className="py-3 text-xs font-bold text-[#6F6F6F]">
                  Job title
                </th>
                <th className="py-3 text-xs font-bold text-[#6F6F6F]">
                  Published date
                </th>
                <th className="py-3 text-xs font-bold text-[#6F6F6F] text-center">
                  Recruitment agent
                </th>
                <th className="py-3 text-xs font-bold text-[#6F6F6F]">
                  Status
                </th>
                <th className="py-3 text-xs font-bold text-[#6F6F6F]">
                  Applied candidates
                </th>
                <th className="py-3 text-xs font-bold text-[#6F6F6F]">
                  Reference
                </th>
              </tr>
            </thead>
            <tbody className="text-gray-700">
              {data?.map((job, idx) => (
                <tr
                  key={idx}
                  className="border-t  border-gray-100 cursor-pointer hover:bg-gray-50"
                  onClick={() => {
                    if (
                      job?.status === EnumJobStatus.DRAFT &&
                      canEditJob(job) &&
                      !isTeamMember
                    ) {
                      navigate(`/requirements/job/edit/${job?._id}`);
                    } else {
                      navigate(`/requirements/job/${job?._id}`);
                    }
                  }}
                >
                  <td className="py-6 text-xs font-medium text-[#6F6F6F] ">
                    {job.title}
                  </td>
                  <td className="py-6 text-xs font-medium text-[#6F6F6F]">
                    {!isNullOrEmpty(job.publishedDate)
                      ? dayjs(job.publishedDate).format("Do MMMM YYYY")
                      : "-"}
                  </td>
                  <td className="py-6 text-xs text-[#6F6F6F] text-center">
                    {job.totalRecruitmentAgents ?? 0}
                  </td>
                  <td className="py-6 text-xs text-[#6F6F6F] capitalize">
                    {getJobStatusLabel(job.status)}
                  </td>
                  <td className="flex py-4 text-xs text-[#6F6F6F]">
                    <div className="relative inline-flex ml-10 self-center items-center justify-center w-8 h-8 rounded-full border border-gray-400 text-sm">
                      {job.totalSubmittedCandidates ?? 0}
                      {job.applied > 0 && (
                        <span className="absolute -top-1 -right-1 text-[8px] w-4 h-4 justify-center items-center flex text-white font-bold bg-green-500 rounded-full border-2 border-white">
                          2
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="py-6 text-xs text-[#6F6F6F]">{job.refNo}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center flex-1 text-center">
          {isTeamMember ? (
            <>
              <p className="text-xs text-[#6F6F6F] font-medium mt-4">
                No job requirements are assigned to you yet.
              </p>
              <p className="text-sm text-primary font-semibold mt-6 whitespace-pre">
                You can review job requirements once they are shared by your
                Admin or Hiring Manager.
              </p>
            </>
          ) : (
            <>
              <p className="text-xs text-[#6F6F6F] font-medium mt-4">
                You currently do not have any proposed candidates.
              </p>
              <p className="text-sm text-primary font-semibold mt-6 whitespace-pre">
                You can submit your job requirement by clicking on the below
                button.
              </p>
            </>
          )}
          {!isTeamMember ? (
            <button
              onClick={() => navigate("/requirements/job/create")}
              className="flex items-center justify-center border bg-green-600 mt-3 rounded-lg px-4 py-2 gap-1 text-white font-medium text-sm"
            >
              Submit job requirement
            </button>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default Requirements;
